import { changeAlertText, isLoading } from '../actions/index';
import { addingTime, sendOnSave, showTimeReminder, forceEmailSave, setBillingCodesSets } from '../actions/time';
import { savingToTimeline } from '../actions/mail';
import { batch } from 'react-redux';
const { checkBillingRules, getTime, getBillingCodeSets } = require('../../api/emailsApi/time');
const ONE_TENTH_HOUR = 360; // equal to 0.10 hours in seconds

/**
 * handleAddTime handles the routing/error when attempting to add time
 * @param {bool} isErrorLoadingTimeConfig state value
 * @param {object} dispatch from calling component
 * @param {bool} shouldSave if add time should save the time record
 * @param {bool} checkActivityTimeline if add time should select activity timeline
 * @param {object} selectedRecords from mail state
 * @param {object} timeFields from mail state
 */
export const handleAddTime = async (isErrorLoadingTimeConfig, dispatch, shouldSave, checkActivityTimeline, selectedRecords, timeFields, pmNamespace = 'litify_pm') => {
  if (isErrorLoadingTimeConfig) {
    dispatch(changeAlertText(`You can't add time, there was an error loading time configuration.`, 'error'));
  } else {
    dispatch(isLoading(true));
    let billingCodeSets;
    try {
      dispatch(addingTime(true));
      const matterIds = Object.values(selectedRecords)?.filter((record) => record.type === `${pmNamespace}__Matter__c`)?.map((record) => record.Id) || [];
      if (matterIds.length > 0 && timeFields?.find((field) => field.name.includes('Activity_Code') || field.name.includes('Task_Code'))) {
        billingCodeSets = await getBillingCodeSets(matterIds);
      }
      if (billingCodeSets?.data) {
        dispatch(setBillingCodesSets(billingCodeSets.data));
      }
    } catch (error) {
      console.log('Error getting billing code sets:', error);
      if (error.response?.status !== 404) { // exclude banner for 404 errors
        dispatch(changeAlertText(`There was an error loading billing code sets.`, 'error'));
      }
    }
    batch(() => {
      if (checkActivityTimeline) {
        dispatch(changeAlertText('You need to save the email activity to save time, so we checked it for you.'));
        dispatch(forceEmailSave(true));
        dispatch(savingToTimeline(true));
      }
      dispatch(showTimeReminder(false));
      dispatch(sendOnSave(shouldSave));
      dispatch(isLoading(false));
    });
  }
};

/**
 * @param {Array} emailIds EmailMessage Record Ids for saved emails
 * @param {string} emailId graph Id for email being processed
 * @param {string} timeNamespace namespace for time record fields
 * @param {object} dispatch from calling component
 * @param {string} ownerEmail email of the inbox owner (required for shared inboxes)
 * @return {Array} of time records retrieved
 */
export const getProcessingTimeRecords = async (emailIds, emailId, timeNamespace, dispatch, ownerEmail) => {
  const timeResponse = await getTime(emailIds, emailId, ownerEmail);
  const timeRecords = timeResponse?.data || [];
  const failedTimeRecords = [];
  const records = [];

  // Check time records for warnings or errors
  for (const record of timeRecords) {
    if (record.errorMessage) {
      const timeRecordFields = Object.fromEntries(Object.entries(record).filter(([key]) => key.includes(timeNamespace) || key === 'ObjectId'));
      timeRecordFields.fixLater = false;
      const rulesBroken = (await checkBillingRules(timeRecordFields))?.data;

      failedTimeRecords.push({ ...record, ...rulesBroken });
    } else {
      records.push({
        ...record,
        saved: !record.temp,
        error: false,
      });
    }
  }

  return [...failedTimeRecords, ...records];
};

/**
 * @param {number} value total seconds
 * @param {string} durationFormat the format in which to display time, comes from T&B settings;
 *
 * @return {string} the formatted time to display
 */
export const secondsToTimeDurationString = (value, durationFormat) => {
  value = Math.max(ONE_TENTH_HOUR, value);
  let formattedTimeDuration = '';

  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = value % 60;

  if (durationFormat === 'hh:mm:ss') {
    formattedTimeDuration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  } else if (durationFormat === 'hh:mm') {
    formattedTimeDuration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  } else if (durationFormat === 'Hour Decimal') {
    const totalMinutes = minutes + seconds / 60;
    const hourDecimal = hours + totalMinutes / 60;
    formattedTimeDuration = hourDecimal.toFixed(2);
  }
  return formattedTimeDuration;
};
